import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

export interface SeloPremiadoItem {
    id: number
    codigo: string
    dataHoraInicio: string
    quantidadeUtilizada: number
    maximo: number
    brindeId: number | null
    brindeTitle: string | null
    brindeDescricao: string | null
    valorDesconto: number | null
}

export interface CriarSelosPayload {
    codigo: string
    titulo: string
    descricao: string
    maximo: number
    quantidadeAGerar: number
    valorDesconto: number
}

export interface CriarSelosResposta {
    criados: number
}

export interface BaixarSeloResposta {
    codigo: string
    imagemBase64: string
}

export interface BaixarTodosResposta {
    nomeArquivo: string
    zipBase64: string
}

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaErroRaw = { codErro: number; mensagem?: string }

function isErroBackend(data: unknown): data is RespostaErroRaw {
    return (
        typeof data === "object" &&
        data !== null &&
        !Array.isArray(data) &&
        "codErro" in data &&
        (data as RespostaErroRaw).codErro > 0
    )
}

function erroParaRespostaRaw(error: unknown): RespostaErroRaw {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem }
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" }
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" }
}

export const obterSelosPremiados = async function (
    authorization: string,
    busca?: string
): Promise<SeloPremiadoItem[] | ErroApi> {
    try {
        const params = busca ? `?busca=${encodeURIComponent(busca)}` : ""
        const response = await api
            .get<SeloPremiadoItem[] | RespostaErroRaw>(`fornecedor/ObterSelosPremiados${params}`, {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        if (response.data === null || response.data === undefined) {
            return []
        }
        return response.data as SeloPremiadoItem[]
    } catch (error) {
        RegistrarLogErro(error, "ObterSelosPremiados", busca ?? "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const criarSelosPremiados = async function (
    authorization: string,
    payload: CriarSelosPayload
): Promise<CriarSelosResposta | ErroApi> {
    try {
        const response = await api
            .post<CriarSelosResposta | RespostaErroRaw>("fornecedor/CriarSelosPremiados", payload, {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        return response.data as CriarSelosResposta
    } catch (error) {
        RegistrarLogErro(error, "CriarSelosPremiados", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const excluirSeloPremiado = async function (
    authorization: string,
    id: number
): Promise<void | ErroApi> {
    try {
        const response = await api
            .delete<RespostaErroRaw | null>(`fornecedor/ExcluirSeloPremiado/${id}`, {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        if (response.data && isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
    } catch (error) {
        RegistrarLogErro(error, "ExcluirSeloPremiado", String(id))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const baixarSeloPremiado = async function (
    authorization: string,
    id: number
): Promise<BaixarSeloResposta | ErroApi> {
    try {
        const response = await api
            .get<BaixarSeloResposta | RespostaErroRaw>(`fornecedor/BaixarSeloPremiado/${id}`, {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        return response.data as BaixarSeloResposta
    } catch (error) {
        RegistrarLogErro(error, "BaixarSeloPremiado", String(id))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const baixarTodosSelosPremiados = async function (
    authorization: string
): Promise<BaixarTodosResposta | ErroApi> {
    try {
        const response = await api
            .get<BaixarTodosResposta | RespostaErroRaw>("fornecedor/BaixarTodosSelosPremiados", {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        return response.data as BaixarTodosResposta
    } catch (error) {
        RegistrarLogErro(error, "BaixarTodosSelosPremiados", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export function baixarBase64(base64: string, nomeArquivo: string, mimeType: string) {
    const bytes = atob(base64)
    const byteArray = new Uint8Array(bytes.length)
    for (let i = 0; i < bytes.length; i++) {
        byteArray[i] = bytes.charCodeAt(i)
    }
    const blob = new Blob([byteArray], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = nomeArquivo
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
}
